import { useState, useEffect } from 'react';
import ConfigService from '../../services/ConfigService';
import defaultChecklistsData from './data/checklistsData.json';

export interface ChecklistItem {
  key: string;
  label: string;
  category?: string;
}

export interface ChecklistsConfig {
  medications: ChecklistItem[];
  roomEquipment: ChecklistItem[];
  ventilationEquipment: ChecklistItem[];
  intubationEquipment: ChecklistItem[];
  departmentAdmission: ChecklistItem[];
  parents: ChecklistItem[];
}

/**
 * Custom hook to fetch and manage checklists configuration from AppConfig
 * Falls back to default data if config is not found or on error
 */
export const useChecklistsConfig = (hospital?: string) => {
  const [checklistsConfig, setChecklistsConfig] = useState<ChecklistsConfig>(defaultChecklistsData as ChecklistsConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      // If no hospital is provided, use default data
      if (!hospital) {
        setChecklistsConfig(defaultChecklistsData as ChecklistsConfig);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await ConfigService.getConfig('NEW_PREEMIE', 'checklists', hospital);

        if (response && response.data) {
          setChecklistsConfig(response.data as ChecklistsConfig);
        } else {
          // Fall back to default data if no config found
          setChecklistsConfig(defaultChecklistsData as ChecklistsConfig);
        }
      } catch (err) {
        console.error('Error fetching checklists config:', err);
        setError(err as Error);
        // Fall back to default data on error
        setChecklistsConfig(defaultChecklistsData as ChecklistsConfig);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [hospital]);

  return { checklistsConfig, loading, error };
};
